'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Search, Bookmark, Star, Folder, User } from 'lucide-react';
import { UserAuth } from '@/app/context/AuthContext';
import ProBadge from '@/components/@generalComponents/ProBadge';

const NavItem = ({ href, icon: Icon, label, active, pro, comingSoon, iconSize = 20, onClick }) => {

	const content = (
		<div
			className={`flex flex-col px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
				active
					? 'bg-white text-purple-500'
					: 'text-gray-700 hover:bg-white/70'
			}`}
		>
			<div className='flex items-center gap-3'>
				<div className={`flex items-center justify-center rounded-md ${active ? 'bg-white' : 'bg-gray-100'}`}>
					<Icon
						size={iconSize}
						className={active ? 'text-purple-500' : 'text-gray-500'}
					/>
				</div>

				<span className='flex-1'>{label}</span>
				{pro && <ProBadge className='ml-2' />}
			</div>
			{comingSoon && (
				<span className='text-xs text-gray-400 pl-8 mt-0.5'>Coming Soon</span>
			)}
		</div>
	);

	if (onClick) return <button className='w-full text-left' onClick={onClick}>{content}</button>;
	if (!href) return <div className='cursor-default'>{content}</div>;
	return <Link href={href}>{content}</Link>;
};

const LeftSidebar = () => {
	const pathname = usePathname();
	const router = useRouter();
	const { user, isPro, logOut } = UserAuth();
	const [accountOpen, setAccountOpen] = useState(false);
	const accountRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (accountRef.current && !accountRef.current.contains(e.target)) {
				setAccountOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Sidebar is only shown for logged-in users
	if (!user) return null;

	const handleLogout = async () => {
		try {
			await logOut();
			setAccountOpen(false);
			router.push('/');
		} catch (err) {
			console.error('Error logging out:', err);
		}
	};

	const isActive = (path) => pathname === path;

	return (
		<aside className='hidden md:flex w-56 shrink-0 flex-col bg-[#F5F5F5] px-3 pb-4 pt-0 sticky self-start top-[var(--topnav-h,84px)] h-[calc(100dvh-var(--topnav-h,84px))] overflow-y-auto'>
			<nav className='flex flex-col gap-1'>
				<NavItem
					href='/explore'
					icon={Search}
					label='Explore'
					active={isActive('/explore')}
				/>
				<NavItem
					href={isPro ? '/following' : undefined}
					onClick={!isPro ? () => router.push('/pro') : undefined}
					icon={Bookmark}
					label='Following'
					active={isActive('/following')}
					pro
				/>
				<NavItem
					href='/bookmarks'
					icon={Star}
					label='Bookmarks'
					active={isActive('/bookmarks')}
				/>
				<NavItem
					icon={Folder}
					iconSize={20}
					label='Collections'
					pro
					comingSoon
				/>
			</nav>

			<div className='mt-auto' ref={accountRef}>
				<button
					onClick={() => setAccountOpen((v) => !v)}
					className='flex w-full items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-white/70'
				>
					<User size={20} className='text-gray-500' />
					<span className='flex-1 text-left'>My Account</span>
				</button>

				{accountOpen && (
					<div className='mt-1 flex flex-col gap-1 rounded-lg bg-white p-2 shadow-sm'>
						{user?.email && (
							<div className='px-3 py-2 text-sm text-gray-500 truncate'>
								{user.email}
							</div>
						)}
						{!isPro && (
							<button
								onClick={() => {
									router.push('/pro');
									setAccountOpen(false);
								}}
								className='rounded-md px-3 py-2 text-left text-sm font-medium text-purple-500 hover:bg-gray-50'
							>
								Upgrade to PRO
							</button>
						)}
						<button
							onClick={() => {
								router.push('/account');
								setAccountOpen(false);
							}}
							className='rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50'
						>
							Manage subscription
						</button>
						<button
							onClick={handleLogout}
							className='rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50'
						>
							Sign out
						</button>
					</div>
				)}
			</div>
		</aside>
	);
};

export default LeftSidebar;
